import { useState, useMemo, useEffect, useCallback } from 'react';
import { BarChart2, Copy, Check, X, ChevronLeft, ChevronRight, Users, ShieldCheck } from 'lucide-react';
import { startOfWeek, endOfWeek, startOfDay, endOfDay, format, addDays, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { useDatabase } from '@/context/DatabaseContext';
import { useAuth } from '@/context/AuthContext';
import { isCompletedStatus } from '@/constants/taskStatuses';
import { useAppStore } from '@/store/appStore';
import { ViewToolbar } from '@/components/shared/view-toolbar';
import { useOrgMembers } from '@/hooks/useOrgMembers';
import { fetchTimeEntries, type TimeEntry } from '@/db/timeEntries';
import { saveReportSnapshot, getSnapshotsForDate, type ReportSnapshot, type SnapshotTask } from '@/db/reportSnapshots';
import type { Task } from '@/types';

type ColumnType = 'week-start' | 'day-start' | 'day-end' | 'week-end';

// ─── helpers ────────────────────────────────────────────────────────────────

function getBounds(referenceDate: Date) {
  return {
    weekStart: startOfWeek(referenceDate, { weekStartsOn: 1 }).getTime(),
    weekEnd: endOfWeek(referenceDate, { weekStartsOn: 1 }).getTime(),
    dayStart: startOfDay(referenceDate).getTime(),
    dayEnd: endOfDay(referenceDate).getTime(),
  };
}

function formatDuration(ms: number | null): string | null {
  if (!ms || ms <= 0) return null;
  const totalMinutes = Math.round(ms / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function taskLine(t: TaskLike, timeType: 'estimate' | 'spent') {
  const duration = formatDuration(timeType === 'estimate' ? t.timeEstimated : (t.timeSpent ?? null));
  return duration ? `  ➔ ${t.name} (${duration})` : `  ➔ ${t.name}`;
}

async function copyText(text: string) {
  try { await navigator.clipboard.writeText(text); } catch {
    const ta = document.createElement('textarea');
    ta.value = text; document.body.appendChild(ta); ta.select();
    document.execCommand('copy'); document.body.removeChild(ta);
  }
}

// ─── Section builders ────────────────────────────────────────────────────────

type TaskLike = Pick<Task, 'id' | 'name' | 'timeEstimated'> & { timeSpent?: number | null };

function sumTime(tasks: TaskLike[], type: 'estimate' | 'spent', extra?: TaskLike[]): string {
  const total = [...tasks, ...(extra || [])].reduce((s, t) => s + (type === 'estimate' ? (t.timeEstimated || 0) : (t.timeSpent ?? 0)), 0);
  return formatDuration(total) || '0m';
}

function buildWeekStart(tasks: TaskLike[]) {
  const lines = ['🌟 Weekly Plans', '— Questa settimana mi concentrerò su:'];
  if (tasks.length > 0) { tasks.forEach(t => lines.push(taskLine(t, 'estimate'))); } else { lines.push('  ➔ Nessun task programmato'); }
  lines.push(`— Totale stimato: ${sumTime(tasks, 'estimate')}`);
  return lines.join('\n');
}

function buildDayStart(tasks: TaskLike[], obstacles: string) {
  const lines = ['📅 Today\'s Plan', '— Oggi lavorerò su:'];
  if (tasks.length > 0) { tasks.forEach(t => lines.push(taskLine(t, 'estimate'))); } else { lines.push('  ➔ Nessun task programmato'); }
  lines.push('— Possibili ostacoli:');
  lines.push(`  ➔ ${obstacles || 'Nessuno'}`);
  lines.push(`— Totale stimato: ${sumTime(tasks, 'estimate')}`);
  return lines.join('\n');
}

function buildDayEnd(tasks: TaskLike[], blockers: string, inProgressTasks?: TaskLike[]) {
  const lines = ['✅ End of Day', '— Cosa ho completato oggi:'];
  if (tasks.length > 0) { tasks.forEach(t => lines.push(taskLine(t, 'spent'))); } else { lines.push('  ➔ Nessun task completato'); }
  if (inProgressTasks && inProgressTasks.length > 0) {
    lines.push('— In corso (con tempo tracciato):');
    inProgressTasks.forEach(t => lines.push(taskLine(t, 'spent')));
  }
  lines.push('— Problemi/Blocchi:');
  lines.push(`  ➔ ${blockers || 'Nessuno'}`);
  lines.push(`— Totale tracciato: ${sumTime(tasks, 'spent', inProgressTasks)}`);
  return lines.join('\n');
}

function buildWeekEnd(tasks: TaskLike[], notCompleted: string, lessons: string, inProgressTasks?: TaskLike[]) {
  const lines = ['🚀 Weekly Closing', '— Obiettivi della settimana completati:'];
  if (tasks.length > 0) { tasks.forEach(t => lines.push(taskLine(t, 'spent'))); } else { lines.push('  ➔ Nessun obiettivo completato'); }
  if (inProgressTasks && inProgressTasks.length > 0) {
    lines.push('— In corso (con tempo tracciato):');
    inProgressTasks.forEach(t => lines.push(taskLine(t, 'spent')));
  }
  lines.push('— Obiettivi non completati e motivi:');
  lines.push(`  ➔ ${notCompleted || 'Nessuno'}`);
  lines.push('— Lezioni apprese / Migliorie per la prossima settimana:');
  lines.push(`  ➔ ${lessons || '—'}`);
  lines.push(`— Totale tracciato: ${sumTime(tasks, 'spent', inProgressTasks)}`);
  return lines.join('\n');
}

// ─── Modal ───────────────────────────────────────────────────────────────────

interface CopyModalProps {
  columnType: Exclude<ColumnType, 'week-start'>;
  tasks: TaskLike[];
  inProgressTasks?: TaskLike[];
  onClose: () => void;
}

function CopyModal({ columnType, tasks, inProgressTasks, onClose }: CopyModalProps) {
  const [field1, setField1] = useState('');
  const [field2, setField2] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    let text = '';
    if (columnType === 'day-start') text = buildDayStart(tasks, field1);
    else if (columnType === 'day-end') text = buildDayEnd(tasks, field1, inProgressTasks);
    else if (columnType === 'week-end') text = buildWeekEnd(tasks, field1, field2, inProgressTasks);
    await copyText(text);
    setCopied(true);
    setTimeout(() => { setCopied(false); onClose(); }, 1200);
  };

  const titles: Record<Exclude<ColumnType, 'week-start'>, string> = {
    'day-start': 'Today\'s Plan — campi manuali',
    'day-end': 'End of Day — campi manuali',
    'week-end': 'Weekly Closing — campi manuali',
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-surface-primary border border-border-primary rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-primary">
          <h2 className="text-sm font-semibold text-text-primary">{titles[columnType]}</h2>
          <button onClick={onClose} className="text-text-quaternary hover:text-text-secondary transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {columnType === 'day-start' && (
            <Field label="Possibili ostacoli" placeholder="es. Riunione alle 15, attesa feedback..." value={field1} onChange={setField1} />
          )}
          {columnType === 'day-end' && (
            <Field label="Problemi/Blocchi" placeholder="es. Nessuno" value={field1} onChange={setField1} />
          )}
          {columnType === 'week-end' && (
            <>
              <Field label="Obiettivi non completati e motivi" placeholder="es. Task X rimandato per..." value={field1} onChange={setField1} />
              <Field label="Lezioni apprese / Migliorie" placeholder="es. Pianificare meglio le attività..." value={field2} onChange={setField2} />
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-border-primary">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-text-secondary hover:bg-surface-hover transition-colors">
            Annulla
          </button>
          <button onClick={handleCopy} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-accent-violet text-white hover:bg-accent-violet-light transition-colors">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copiato!' : 'Copia report'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, placeholder, value, onChange }: {
  label: string; placeholder: string; value: string; onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-text-tertiary mb-1">{label}</label>
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={2}
        className="w-full px-3 py-2 text-sm bg-surface-secondary border border-border-primary rounded-lg text-text-primary placeholder-text-quaternary resize-none focus:outline-none focus:ring-1 focus:ring-accent-violet/50" />
    </div>
  );
}

// ─── Column ──────────────────────────────────────────────────────────────────

function ReportColumn({ title, subtitle, emoji, tasks, emptyLabel, timeType, onCopy, directCopy, onTaskClick, inProgressTasks, totalHours, onFreeze, frozen, frozenDate }: {
  title: string; subtitle: string; emoji: string; tasks: TaskLike[]; emptyLabel: string;
  timeType: 'estimate' | 'spent'; onCopy: () => Promise<void> | void; directCopy?: boolean;
  onTaskClick?: (taskId: string) => void;
  inProgressTasks?: TaskLike[];
  totalHours?: string | null;
  onFreeze?: () => void;
  frozen?: boolean;
  frozenDate?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await onCopy();
    if (directCopy) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className={`flex flex-col bg-surface-secondary border rounded-xl overflow-hidden min-h-0 ${frozen ? 'border-emerald-500/30' : 'border-border-primary'}`}>
      <div className="px-4 py-3 border-b border-border-primary">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-base">{emoji}</span>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-text-primary truncate">{title}</p>
              <p className="text-[10px] text-text-quaternary truncate">{subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {tasks.length > 0 && (
              <span className="text-[10px] font-semibold text-text-quaternary bg-surface-tertiary px-1.5 py-0.5 rounded-full">
                {tasks.length}
              </span>
            )}
            {totalHours && (
              <span className="text-[10px] font-medium text-accent-violet bg-accent-violet/10 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                {totalHours}
              </span>
            )}
            <button onClick={handleCopy} className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium border transition-colors ${copied ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600' : 'bg-surface-primary border-border-primary text-text-secondary hover:bg-surface-hover hover:text-text-primary'}`}>
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copiato!' : 'Copia'}
            </button>
          </div>
        </div>
        {/* Freeze row */}
        {onFreeze && !frozen && (
          <button onClick={onFreeze} className="mt-2 flex items-center gap-1 text-[10px] font-medium text-accent-violet hover:text-accent-violet-light transition-colors">
            <ShieldCheck className="w-3 h-3" />
            Congela
          </button>
        )}
        {frozen && (
          <div className="mt-2 flex items-center gap-1 text-[10px] font-medium text-emerald-600">
            <ShieldCheck className="w-3 h-3" />
            Congelato{frozenDate ? ` ${frozenDate}` : ''}
          </div>
        )}
      </div>
      <div className="p-2 space-y-1 min-h-[80px] xl:overflow-y-auto xl:min-h-0 xl:flex-1">
        {tasks.length === 0 && (!inProgressTasks || inProgressTasks.length === 0) ? (
          <p className="text-xs text-text-quaternary text-center py-6 italic">{emptyLabel}</p>
        ) : (
          <>
            {tasks.map(t => {
              const duration = formatDuration(timeType === 'estimate' ? t.timeEstimated : (t.timeSpent ?? null));
              return (
                <div key={t.id} onClick={() => onTaskClick?.(t.id)} className="flex items-start justify-between gap-2 px-2 py-1.5 rounded-lg hover:bg-surface-hover/50 transition-colors cursor-pointer">
                  <div className="flex items-start gap-2 min-w-0">
                    <span className="text-text-quaternary mt-0.5 shrink-0 text-xs">➔</span>
                    <span className="text-xs text-text-primary leading-relaxed">{t.name}</span>
                  </div>
                  {duration && (
                    <span className="shrink-0 text-[10px] font-medium text-text-quaternary bg-surface-tertiary px-1.5 py-0.5 rounded-full whitespace-nowrap">
                      {duration}
                    </span>
                  )}
                </div>
              );
            })}
            {inProgressTasks && inProgressTasks.length > 0 && (
              <>
                <div className="flex items-center gap-2 px-2 pt-3 pb-1">
                  <div className="h-px flex-1 bg-border-primary/50" />
                  <span className="text-[10px] font-semibold text-amber-500 uppercase tracking-wider shrink-0">In corso ({inProgressTasks.length})</span>
                  <div className="h-px flex-1 bg-border-primary/50" />
                </div>
                {inProgressTasks.map(t => {
                  const duration = formatDuration(t.timeSpent ?? null);
                  return (
                    <div key={t.id} onClick={() => onTaskClick?.(t.id)} className="flex items-start justify-between gap-2 px-2 py-1.5 rounded-lg hover:bg-surface-hover/50 transition-colors cursor-pointer">
                      <div className="flex items-start gap-2 min-w-0">
                        <span className="text-amber-500 mt-0.5 shrink-0 text-xs">⏳</span>
                        <span className="text-xs text-text-primary leading-relaxed">{t.name}</span>
                      </div>
                      {duration && (
                        <span className="shrink-0 text-[10px] font-medium text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                          {duration}
                        </span>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Date navigation ─────────────────────────────────────────────────────────

function DateNav({ date, onPrev, onNext, onToday, isToday }: {
  date: Date; onPrev: () => void; onNext: () => void; onToday: () => void; isToday: boolean;
}) {
  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
  const weekLabel = `${format(weekStart, 'd MMM', { locale: it })} – ${format(weekEnd, 'd MMM', { locale: it })}`;
  const dayLabel = format(date, 'EEEE d MMMM', { locale: it });

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-0.5 bg-surface-secondary border border-border-primary rounded-lg">
        <button onClick={onPrev} className="p-1.5 hover:bg-surface-hover rounded-l-lg transition-colors text-text-tertiary hover:text-text-primary">
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <button onClick={onNext} className="p-1.5 hover:bg-surface-hover rounded-r-lg transition-colors text-text-tertiary hover:text-text-primary">
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="text-[11px] text-text-secondary font-medium">
        <span className="capitalize">{dayLabel}</span>
        <span className="text-text-quaternary mx-1.5">·</span>
        <span className="text-text-quaternary">Settimana {weekLabel}</span>
      </div>
      {!isToday && (
        <button onClick={onToday} className="text-[10px] font-medium text-accent-violet hover:text-accent-violet-light bg-accent-violet/10 px-2 py-0.5 rounded-full transition-colors">
          Oggi
        </button>
      )}
    </div>
  );
}

// ─── Member selector ─────────────────────────────────────────────────────────

function MemberSelector({ selectedUserId, onSelect, currentUserId }: {
  selectedUserId: string | null; onSelect: (userId: string | null) => void; currentUserId: string;
}) {
  const { orgMembers, loaded } = useOrgMembers();
  const [isOpen, setIsOpen] = useState(false);

  if (!loaded || orgMembers.length <= 1) return null;

  const selectedMember = selectedUserId
    ? orgMembers.find(m => m.user_id === selectedUserId)
    : null;

  const displayName = selectedMember
    ? selectedMember.display_name || selectedMember.email || 'Membro'
    : 'Il mio report';

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-surface-secondary border border-border-primary text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
      >
        <Users className="w-3.5 h-3.5" />
        <span className="max-w-[120px] truncate">{displayName}</span>
      </button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-30 bg-surface-primary border border-border-primary rounded-lg shadow-2xl min-w-[200px] py-1 animate-fade-in">
          <button
            onClick={() => { onSelect(null); setIsOpen(false); }}
            className={`w-full flex items-center gap-2 px-3 py-2 text-left text-[11px] hover:bg-surface-hover transition-colors ${!selectedUserId ? 'text-accent-violet font-medium' : 'text-text-primary'}`}
          >
            Il mio report
            {!selectedUserId && <Check className="w-3 h-3 ml-auto text-accent-violet" />}
          </button>
          {orgMembers.filter(m => m.user_id !== currentUserId).map(m => {
            const isSelected = m.user_id === selectedUserId;
            return (
              <button
                key={m.user_id}
                onClick={() => { onSelect(m.user_id); setIsOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-[11px] hover:bg-surface-hover transition-colors ${isSelected ? 'text-accent-violet font-medium' : 'text-text-primary'}`}
              >
                <span className="truncate">{m.display_name || m.email || m.user_id.slice(0, 8)}</span>
                {isSelected && <Check className="w-3 h-3 ml-auto text-accent-violet" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main view ───────────────────────────────────────────────────────────────

export function ReportView() {
  const { tasks } = useDatabase();
  const { user } = useAuth();
  const setMobileSidebarOpen = useAppStore((state) => state.setMobileSidebarOpen);
  const setSelectedTaskId = useAppStore((state) => state.setSelectedTaskId);
  const [activeColumn, setActiveColumn] = useState<{ type: Exclude<ColumnType, 'week-start'>; tasks?: TaskLike[]; inProgressTasks?: TaskLike[] } | null>(null);
  const [viewDate, setViewDate] = useState(() => new Date());
  const [selectedMember, setSelectedMember] = useState<string | null>(null);
  // Per-column snapshots: keyed by column type
  const [columnSnapshots, setColumnSnapshots] = useState<Record<string, ReportSnapshot | null>>({});
  const [_snapshotChecked, setSnapshotChecked] = useState(false);
  const [freezingColumn, setFreezingColumn] = useState<string | null>(null);

  const isToday = isSameDay(viewDate, new Date());
  const targetUserId = selectedMember || user?.id;

  // Load time entries for the week to determine which tasks had time tracked
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  useEffect(() => {
    const ws = startOfWeek(viewDate, { weekStartsOn: 1 });
    const we = endOfWeek(viewDate, { weekStartsOn: 1 });
    const startDate = format(ws, 'yyyy-MM-dd');
    const endDate = format(we, 'yyyy-MM-dd');
    let cancelled = false;
    fetchTimeEntries({ startDate, endDate, userId: targetUserId || undefined })
      .then(entries => { if (!cancelled) setTimeEntries(entries); })
      .catch(() => { if (!cancelled) setTimeEntries([]); });
    return () => { cancelled = true; };
  }, [viewDate, targetUserId]);

  const { weekTasks, dayTasks, completedToday, completedWeek, inProgressToday, inProgressWeek } = useMemo(() => {
    const { weekStart, weekEnd, dayStart, dayEnd } = getBounds(viewDate);
    const isAssigned = (t: Task) => !!targetUserId && t.assignees.includes(targetUserId);
    const _updatedAtMs = (t: Task) => t.updatedAt ? new Date(t.updatedAt).getTime() : 0;
    const todayStr = format(viewDate, 'yyyy-MM-dd');

    // Build sets of task IDs that have time entries for today / this week
    const taskIdsWithTimeToday = new Set<string>();
    const taskIdsWithTimeWeek = new Set<string>();
    const timeTodayByTask: Record<string, number> = {};
    const timeWeekByTask: Record<string, number> = {};

    for (const te of timeEntries) {
      taskIdsWithTimeWeek.add(te.taskId);
      timeWeekByTask[te.taskId] = (timeWeekByTask[te.taskId] || 0) + te.duration;
      if (te.date === todayStr) {
        taskIdsWithTimeToday.add(te.taskId);
        timeTodayByTask[te.taskId] = (timeTodayByTask[te.taskId] || 0) + te.duration;
      }
    }

    // Week column: tasks scheduled/due this week + overdue tasks
    const weekTasks = tasks.filter(t => {
      if (!isAssigned(t) || isCompletedStatus(t.status)) return false;
      const startInWeek = t.startDate != null && t.startDate >= weekStart && t.startDate <= weekEnd;
      const dueInWeek = t.dueDate != null && t.dueDate >= weekStart && t.dueDate <= weekEnd;
      const startedBeforeDueThisWeek = t.startDate != null && t.startDate < weekStart && t.dueDate != null && t.dueDate >= weekStart && t.dueDate <= weekEnd;
      const overdue = t.dueDate != null && t.dueDate < dayStart;
      return startInWeek || dueInWeek || startedBeforeDueThisWeek || overdue;
    });

    // Day column: tasks scheduled/due today + overdue
    const dayTasks = tasks.filter(t => {
      if (!isAssigned(t) || isCompletedStatus(t.status)) return false;
      const startToday = t.startDate != null && t.startDate >= dayStart && t.startDate <= dayEnd;
      const dueToday = t.dueDate != null && t.dueDate >= dayStart && t.dueDate <= dayEnd;
      const overdue = t.dueDate != null && t.dueDate < dayStart;
      const inProgress = t.status === 'in_progress';
      return startToday || dueToday || overdue || inProgress;
    });

    // In-progress tasks with actual time entries in the period
    const inProgressToday = tasks
      .filter(t =>
        isAssigned(t) && !isCompletedStatus(t.status) && taskIdsWithTimeToday.has(t.id)
      )
      .map(t => ({ ...t, timeSpent: timeTodayByTask[t.id] || 0 }));

    const inProgressWeek = tasks
      .filter(t =>
        isAssigned(t) && !isCompletedStatus(t.status) && taskIdsWithTimeWeek.has(t.id)
      )
      .map(t => ({ ...t, timeSpent: timeWeekByTask[t.id] || 0 }));

    return {
      weekTasks,
      dayTasks,
      completedToday: tasks
        .filter(t =>
          isAssigned(t) && isCompletedStatus(t.status) &&
          (taskIdsWithTimeToday.has(t.id) || (t.dateCreated >= dayStart && t.dateCreated <= dayEnd))
        )
        .map(t => ({ ...t, timeSpent: timeTodayByTask[t.id] || 0 })),
      completedWeek: tasks
        .filter(t =>
          isAssigned(t) && isCompletedStatus(t.status) &&
          (taskIdsWithTimeWeek.has(t.id) || (t.dateCreated >= weekStart && t.dateCreated <= weekEnd))
        )
        .map(t => ({ ...t, timeSpent: timeWeekByTask[t.id] || 0 })),
      inProgressToday,
      inProgressWeek,
    };
  }, [tasks, targetUserId, viewDate, timeEntries]);

  const weekStart = startOfWeek(viewDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(viewDate, { weekStartsOn: 1 });
  const weekLabel = `${format(weekStart, 'd MMM', { locale: it })} – ${format(weekEnd, 'd MMM', { locale: it })}`;
  const dayLabel = format(viewDate, 'd MMMM yyyy', { locale: it });
  const snapshotDateStr = format(viewDate, 'yyyy-MM-dd');

  const weekStartStr = format(weekStart, 'yyyy-MM-dd');

  // Load all snapshots for this date (and week-start for weekly column)
  useEffect(() => {
    if (!user?.id || !targetUserId) return;
    let cancelled = false;
    setSnapshotChecked(false);
    setColumnSnapshots({});

    // Fetch daily snapshots for this date + weekly snapshot keyed by week start
    Promise.all([
      getSnapshotsForDate(user.id, targetUserId, snapshotDateStr),
      snapshotDateStr !== weekStartStr
        ? getSnapshotsForDate(user.id, targetUserId, weekStartStr)
        : Promise.resolve([]),
    ]).then(([dailySnaps, weekSnaps]) => {
      if (cancelled) return;
      const map: Record<string, ReportSnapshot | null> = {};
      // Daily snapshots: all types for this specific date
      for (const s of dailySnaps) {
        map[s.snapshotType] = s;
      }
      // Week snapshots: only week-level types (avoid leaking day snapshots from week start date)
      for (const s of weekSnaps) {
        if (s.snapshotType === 'week-start' || s.snapshotType === 'week-end') {
          map[s.snapshotType] = s;
        }
      }
      setColumnSnapshots(map);
      setSnapshotChecked(true);
    }).catch(() => {
      if (!cancelled) setSnapshotChecked(true);
    });
    return () => { cancelled = true; };
  }, [user?.id, targetUserId, snapshotDateStr, weekStartStr]);

  const toSnapshotTask = (t: Task & { timeSpent?: number | null }): SnapshotTask => ({
    id: t.id, name: t.name, status: t.status, priority: t.priority || 'normal',
    timeEstimated: t.timeEstimated, timeSpent: t.timeSpent ?? null,
  });

  // Generic freeze handler per column
  const handleFreezeColumn = useCallback(async (colType: ColumnType, colTasks: SnapshotTask[], inProgress?: SnapshotTask[]) => {
    if (!user?.id || !targetUserId || freezingColumn) return;
    setFreezingColumn(colType);
    try {
      const isWeekCol = colType === 'week-start';
      const snap = await saveReportSnapshot({
        userId: user.id,
        targetUserId,
        snapshotDate: isWeekCol ? weekStartStr : snapshotDateStr,
        weekStart: weekStartStr,
        weekEnd: format(weekEnd, 'yyyy-MM-dd'),
        snapshotType: colType,
        weekTasks: isWeekCol ? colTasks : [],
        dayTasks: colType === 'day-start' ? colTasks : [],
        completedToday: colType === 'day-end' ? colTasks : [],
        completedWeek: colType === 'week-end' ? colTasks : [],
        inProgressToday: colType === 'day-end' ? (inProgress || []) : [],
        inProgressWeek: colType === 'week-end' ? (inProgress || []) : [],
      });
      setColumnSnapshots(prev => ({ ...prev, [colType]: snap }));
    } catch {
      // silent
    } finally {
      setFreezingColumn(null);
    }
  }, [user?.id, targetUserId, snapshotDateStr, weekStartStr, weekEnd, freezingColumn]);

  const isPastDate = !isToday && viewDate < new Date();
  // Week columns: freezable only during the current week
  const isCurrentWeek = (() => {
    const now = new Date();
    const nowWeekStart = startOfWeek(now, { weekStartsOn: 1 }).getTime();
    const nowWeekEnd = endOfWeek(now, { weekStartsOn: 1 }).getTime();
    return viewDate.getTime() >= nowWeekStart && viewDate.getTime() <= nowWeekEnd;
  })();
  // Day columns: freezable only on the current day AND only for yourself
  const isOwnReport = !selectedMember || selectedMember === user?.id;
  const canFreezeDay = isToday && isOwnReport;
  const canFreezeWeek = isCurrentWeek && isOwnReport;

  // Totals per column
  const sumEstimated = (arr: Task[] | SnapshotTask[]) => arr.reduce((s, t) => s + (t.timeEstimated || 0), 0);
  const sumSpent = (arr: (Task | SnapshotTask | TaskLike)[]) => arr.reduce((s, t) => s + ((t as Record<string, unknown>).timeSpent as number || 0), 0);
  const weekEstTotal = formatDuration(sumEstimated(weekTasks));
  const dayEstTotal = formatDuration(sumEstimated(dayTasks));
  const dayEndSpentTotal = formatDuration(sumSpent(completedToday) + sumSpent(inProgressToday));
  const weekEndSpentTotal = formatDuration(sumSpent(completedWeek) + sumSpent(inProgressWeek));

  const handleWeekStartCopy = async () => {
    await copyText(buildWeekStart(weekTasks));
  };

  return (
    <div className="flex flex-col w-full h-full bg-surface-primary overflow-y-auto xl:overflow-hidden">
      <ViewToolbar
        icon={<BarChart2 className="w-4 h-4 text-accent-violet" />}
        title="Report"
        subtitle={`${dayLabel} · Settimana ${weekLabel}`}
        onOpenSidebar={() => setMobileSidebarOpen(true)}
        rightContent={
          <div className="flex items-center flex-wrap justify-end gap-3">
            <DateNav
              date={viewDate}
              onPrev={() => setViewDate(d => addDays(d, -1))}
              onNext={() => setViewDate(d => addDays(d, 1))}
              onToday={() => setViewDate(new Date())}
              isToday={isToday}
            />
            {user?.id && (
              <MemberSelector
                selectedUserId={selectedMember}
                onSelect={setSelectedMember}
                currentUserId={user.id}
              />
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 p-4 xl:min-h-0 xl:flex-1">
        {/* Week Start column */}
        {columnSnapshots['week-start'] ? (
          <SnapshotColumn emoji="🌟" title="Inizio settimana" subtitle={`Task settimana ${weekLabel}`}
            tasks={columnSnapshots['week-start']!.weekTasks} emptyLabel="Nessun task" timeType="estimate"
            totalHours={formatDuration(sumEstimated(columnSnapshots['week-start']!.weekTasks))}
            frozenDate={format(new Date(columnSnapshots['week-start']!.createdAt), "d MMM", { locale: it })}
            onCopy={() => copyText(buildWeekStart(columnSnapshots['week-start']!.weekTasks))} directCopy onTaskClick={setSelectedTaskId} />
        ) : isPastDate && !canFreezeWeek ? (
          <NoDataColumn emoji="🌟" title="Inizio settimana" subtitle={`Task settimana ${weekLabel}`} />
        ) : (
          <ReportColumn emoji="🌟" title="Inizio settimana" subtitle={`Task settimana ${weekLabel}`}
            tasks={weekTasks} emptyLabel={isCurrentWeek ? "Nessun task assegnato questa settimana" : `Nessun task assegnato (${weekLabel})`} timeType="estimate"
            onCopy={handleWeekStartCopy} directCopy onTaskClick={setSelectedTaskId}
            totalHours={weekEstTotal}
            onFreeze={canFreezeWeek ? () => handleFreezeColumn('week-start', weekTasks.map(toSnapshotTask)) : undefined}
            frozen={false} />
        )}

        {/* Day Start column */}
        {columnSnapshots['day-start'] ? (
          <SnapshotColumn emoji="📅" title="Inizio giornata" subtitle={`Task del ${dayLabel}`}
            tasks={columnSnapshots['day-start']!.dayTasks} emptyLabel="Nessun task" timeType="estimate"
            totalHours={formatDuration(sumEstimated(columnSnapshots['day-start']!.dayTasks))}
            frozenDate={format(new Date(columnSnapshots['day-start']!.createdAt), "d MMM", { locale: it })}
            onCopy={() => setActiveColumn({ type: 'day-start', tasks: columnSnapshots['day-start']!.dayTasks })} onTaskClick={setSelectedTaskId} />
        ) : isPastDate && !canFreezeDay ? (
          <NoDataColumn emoji="📅" title="Inizio giornata" subtitle={`Task del ${dayLabel}`} />
        ) : (
          <ReportColumn emoji="📅" title="Inizio giornata" subtitle={isToday ? `Task di oggi · ${dayLabel}` : `Task del ${dayLabel}`}
            tasks={dayTasks} emptyLabel={isToday ? "Nessun task assegnato oggi" : `Nessun task assegnato (${dayLabel})`} timeType="estimate"
            onCopy={() => setActiveColumn({ type: 'day-start' })} onTaskClick={setSelectedTaskId}
            totalHours={dayEstTotal}
            onFreeze={canFreezeDay ? () => handleFreezeColumn('day-start', dayTasks.map(toSnapshotTask)) : undefined}
            frozen={false} />
        )}

        {/* Day End column */}
        {columnSnapshots['day-end'] ? (
          <SnapshotColumn emoji="✅" title="Fine giornata" subtitle="Completati"
            tasks={columnSnapshots['day-end']!.completedToday} emptyLabel="Nessun attività" timeType="spent"
            inProgressTasks={columnSnapshots['day-end']!.inProgressToday}
            totalHours={formatDuration(sumSpent(columnSnapshots['day-end']!.completedToday) + sumSpent(columnSnapshots['day-end']!.inProgressToday))}
            frozenDate={format(new Date(columnSnapshots['day-end']!.createdAt), "d MMM", { locale: it })}
            onCopy={() => setActiveColumn({ type: 'day-end', tasks: columnSnapshots['day-end']!.completedToday, inProgressTasks: columnSnapshots['day-end']!.inProgressToday })} onTaskClick={setSelectedTaskId} />
        ) : isPastDate && !canFreezeDay ? (
          <NoDataColumn emoji="✅" title="Fine giornata" subtitle="Completati" />
        ) : (
          <ReportColumn emoji="✅" title="Fine giornata" subtitle={isToday ? "Completati oggi" : `Completati il ${dayLabel}`}
            tasks={completedToday} emptyLabel={isToday ? "Nessun attività oggi" : `Nessun attività (${dayLabel})`} timeType="spent"
            onCopy={() => setActiveColumn({ type: 'day-end' })} onTaskClick={setSelectedTaskId}
            inProgressTasks={inProgressToday}
            totalHours={dayEndSpentTotal}
            onFreeze={canFreezeDay ? () => handleFreezeColumn('day-end', completedToday.map(toSnapshotTask), inProgressToday.map(toSnapshotTask)) : undefined}
            frozen={false} />
        )}

        {/* Week End column */}
        {columnSnapshots['week-end'] ? (
          <SnapshotColumn emoji="🚀" title="Fine settimana" subtitle={`Completati ${weekLabel}`}
            tasks={columnSnapshots['week-end']!.completedWeek} emptyLabel="Nessun attività" timeType="spent"
            inProgressTasks={columnSnapshots['week-end']!.inProgressWeek}
            totalHours={formatDuration(sumSpent(columnSnapshots['week-end']!.completedWeek) + sumSpent(columnSnapshots['week-end']!.inProgressWeek))}
            frozenDate={format(new Date(columnSnapshots['week-end']!.createdAt), "d MMM", { locale: it })}
            onCopy={() => setActiveColumn({ type: 'week-end', tasks: columnSnapshots['week-end']!.completedWeek, inProgressTasks: columnSnapshots['week-end']!.inProgressWeek })} onTaskClick={setSelectedTaskId} />
        ) : isPastDate && !canFreezeWeek ? (
          <NoDataColumn emoji="🚀" title="Fine settimana" subtitle={`Completati ${weekLabel}`} />
        ) : (
          <ReportColumn emoji="🚀" title="Fine settimana" subtitle={`Completati ${weekLabel}`}
            tasks={completedWeek} emptyLabel={isCurrentWeek ? "Nessun attività questa settimana" : `Nessun attività (${weekLabel})`} timeType="spent"
            onCopy={() => setActiveColumn({ type: 'week-end' })} onTaskClick={setSelectedTaskId}
            inProgressTasks={inProgressWeek}
            totalHours={weekEndSpentTotal}
            onFreeze={canFreezeWeek ? () => handleFreezeColumn('week-end', completedWeek.map(toSnapshotTask), inProgressWeek.map(toSnapshotTask)) : undefined}
            frozen={false} />
        )}
      </div>

      {activeColumn && (
        <CopyModal
          columnType={activeColumn.type}
          tasks={activeColumn.tasks ?? (activeColumn.type === 'day-start' ? dayTasks : activeColumn.type === 'day-end' ? completedToday : completedWeek)}
          inProgressTasks={activeColumn.inProgressTasks ?? (activeColumn.type === 'day-end' ? inProgressToday : activeColumn.type === 'week-end' ? inProgressWeek : undefined)}
          onClose={() => setActiveColumn(null)}
        />
      )}
    </div>
  );
}

// ─── No data placeholder column ──────────────────────────────────────────────

function NoDataColumn({ title, subtitle, emoji }: {
  title: string; subtitle: string; emoji: string;
}) {
  return (
    <div className="flex flex-col bg-surface-secondary border border-border-primary rounded-xl overflow-hidden min-h-0">
      <div className="px-4 py-3 border-b border-border-primary">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-base opacity-40">{emoji}</span>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-text-tertiary truncate">{title}</p>
            <p className="text-[10px] text-text-quaternary truncate">{subtitle}</p>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 min-h-[80px]">
        <div className="text-center">
          <BarChart2 className="w-8 h-8 text-text-quaternary mx-auto mb-2 opacity-30" />
          <p className="text-[11px] text-text-tertiary font-medium">Dati non disponibili</p>
          <p className="text-[10px] text-text-quaternary mt-0.5">Non congelato</p>
        </div>
      </div>
    </div>
  );
}

// ─── Snapshot column (frozen, with copy button) ─────────────────────────────

function SnapshotColumn({ title, subtitle, emoji, tasks, emptyLabel, timeType, inProgressTasks, totalHours, frozenDate, onCopy, directCopy, onTaskClick }: {
  title: string; subtitle: string; emoji: string; tasks: SnapshotTask[]; emptyLabel: string;
  timeType: 'estimate' | 'spent'; inProgressTasks?: SnapshotTask[];
  totalHours?: string | null; frozenDate?: string;
  onCopy?: () => Promise<void> | void; directCopy?: boolean;
  onTaskClick?: (taskId: string) => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!onCopy) return;
    await onCopy();
    if (directCopy) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="flex flex-col bg-surface-secondary border border-emerald-500/30 rounded-xl overflow-hidden min-h-0">
      <div className="px-4 py-3 border-b border-border-primary">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-base">{emoji}</span>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-text-primary truncate">{title}</p>
              <p className="text-[10px] text-text-quaternary truncate">{subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {tasks.length > 0 && (
              <span className="text-[10px] font-semibold text-text-quaternary bg-surface-tertiary px-1.5 py-0.5 rounded-full">
                {tasks.length}
              </span>
            )}
            {totalHours && (
              <span className="text-[10px] font-medium text-emerald-600 bg-emerald-500/10 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                {totalHours}
              </span>
            )}
            {onCopy && (
              <button onClick={handleCopy} className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium border transition-colors ${copied ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600' : 'bg-surface-primary border-border-primary text-text-secondary hover:bg-surface-hover hover:text-text-primary'}`}>
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? 'Copiato!' : 'Copia'}
              </button>
            )}
          </div>
        </div>
        <div className="mt-2 flex items-center gap-1 text-[10px] font-medium text-emerald-600">
          <ShieldCheck className="w-3 h-3" />
          Congelato{frozenDate ? ` ${frozenDate}` : ''}
        </div>
      </div>
      <div className="p-2 space-y-1 min-h-[80px] xl:overflow-y-auto xl:min-h-0 xl:flex-1">
        {tasks.length === 0 && (!inProgressTasks || inProgressTasks.length === 0) ? (
          <p className="text-xs text-text-quaternary text-center py-6 italic">{emptyLabel}</p>
        ) : (
          <>
            {tasks.map(t => {
              const duration = formatDuration(timeType === 'estimate' ? t.timeEstimated : (t.timeSpent ?? null));
              return (
                <div key={t.id} onClick={() => onTaskClick?.(t.id)} className="flex items-start justify-between gap-2 px-2 py-1.5 rounded-lg hover:bg-surface-hover/50 transition-colors cursor-pointer">
                  <div className="flex items-start gap-2 min-w-0">
                    <span className="text-text-quaternary mt-0.5 shrink-0 text-xs">➔</span>
                    <span className="text-xs text-text-primary leading-relaxed">{t.name}</span>
                  </div>
                  {duration && (
                    <span className="shrink-0 text-[10px] font-medium text-text-quaternary bg-surface-tertiary px-1.5 py-0.5 rounded-full whitespace-nowrap">
                      {duration}
                    </span>
                  )}
                </div>
              );
            })}
            {inProgressTasks && inProgressTasks.length > 0 && (
              <>
                <div className="flex items-center gap-2 px-2 pt-3 pb-1">
                  <div className="h-px flex-1 bg-border-primary/50" />
                  <span className="text-[10px] font-semibold text-amber-500 uppercase tracking-wider shrink-0">In corso ({inProgressTasks.length})</span>
                  <div className="h-px flex-1 bg-border-primary/50" />
                </div>
                {inProgressTasks.map(t => {
                  const duration = formatDuration(t.timeSpent ?? null);
                  return (
                    <div key={t.id} onClick={() => onTaskClick?.(t.id)} className="flex items-start justify-between gap-2 px-2 py-1.5 rounded-lg hover:bg-surface-hover/50 transition-colors cursor-pointer">
                      <div className="flex items-start gap-2 min-w-0">
                        <span className="text-amber-500 mt-0.5 shrink-0 text-xs">⏳</span>
                        <span className="text-xs text-text-primary leading-relaxed">{t.name}</span>
                      </div>
                      {duration && (
                        <span className="shrink-0 text-[10px] font-medium text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                          {duration}
                        </span>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
